import { html } from '../../mixins/katapult-element.js';
import { KatapultEditorTemplate } from './katapult-editor-template.js';
import '../paper-header/paper-header.js';
import '@polymer/paper-toast/paper-toast.js';
import '../k-logic-editor/k-logic-editor.js';
import '../katapult-switcher/katapult-switcher.js';
import '../katapult-color-picker/katapult-color-picker-button.js';
import '../katapult-color-picker/katapult-color-picker-dialog.js';
import '../material-icon/material-icon.js';
import '../material-icon-picker/material-icon-picker.js';
import { PortalStyle } from './katapult-model-editor-style.js';
import { Path } from '../../modules/Path.js';
import { CamelCase } from '../../modules/CamelCase.js';
import { ContainsIllegalCharacters, IllegalCharacters } from '../../modules/ContainsIllegalCharacters.js';
import { GeneratePushID } from '../../modules/GeneratePushID.js';
import { snakeCase } from 'change-case';
import { DialogService } from '../../modules/DialogService.js';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import { DEFAULT_SCHEMAS, PORTAL_SCHEMAS } from '../../modules/KLogicSchemas.js';
import './workflow-type-editor-elements/workflow-action-list.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { stripIndent } from 'common-tags';
import { confirmSyncPortalConfig, syncPortalConfigs } from './SyncedPortalConfigHelpers.js';

/** Portal App Type Editor (main) */
class KatapultPortalAppTypeEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style} ${PortalStyle}
      <style>
        [hidden] {
          display: none !important;
        }

        katapult-switcher {
          font-size: 11pt;
          font-weight: initial;
          --katapult-switcher-paper-cell: {
            padding: 4px 12px;
          };
        }

        katapult-switcher.WARNING {
          --katapult-switcher-paper-cell-selected: {
            background: var(--paper-amber-500);
            color: var(--primary-text-color);
          };
        }

        katapult-switcher.ERROR {
          --katapult-switcher-paper-cell-selected: {
            background: var(--paper-red-500);
          };
        }

        #iconPickerDialog {
          --katapult-dialog-legacy-body: {
            padding: 0;
            overflow: hidden;
          };
        }

        #renameDeleteStatusDialog strong {
          color: var(--secondary-color);
        }

        .status-name {
          user-select: text;
          font-size: 12pt;
          gap: 16px;
        }
        .manage-status-button {
          --paper-menu-button-content: {
            border-radius: 16px;
            white-space: nowrap;
          };
        }

        .dialog-option {
          min-width: 5.14em;
        }

        .vertical-dividers {
          text-align: center;
        }
        .vertical-dividers paper-row paper-cell:not(:last-of-type) {
          border-right: 1px solid rgba(0, 0, 0, 0.06);
        }
        .vertical-dividers paper-checkbox {
          --paper-checkbox-label-spacing: 0;
        }

        .active-indicator {
          border: 1px solid var(--primary-text-color);
          background: var(--paper-green-500);
          border-radius: 24px;
          height: 12px;
          width: 12px;
        }
        .access-group-chips {
          display: flex;
          gap: 8px;
        }
        .access-group-chip {
          border: 1px solid var(--primary-text-color);
          border-radius: 24px;
          height: 24px;
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--paper-grey-100);
        }
        .chip {
          background: rgba(0, 0, 0, 0.05);
          padding: 0.3em;
          padding-right: 0.6em;
          border-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.4em;
          opacity: 0.8;
          font-style: italic;
        }
        .chip > material-icon {
          font-size: 1.4em;
        }
      </style>
      <paper-toast id="toast"></paper-toast>

      <katapult-firebase-worker path="photoheight/companies" data="{{companies}}"></katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes" data="{{otherAttributes}}"></katapult-firebase-worker>

      <katapult-firebase-worker
        path="[[modelSpaceDetails.path]]/portal_config/export_options"
        data="{{exportOptions}}"
      ></katapult-firebase-worker>

      <katapult-firebase-worker
        path="[[modelSpaceDetails.path]]/portal_config/timeline_models"
        data="{{timelineModels}}"
      ></katapult-firebase-worker>

      <katapult-firebase-worker
        path="[[modelSpaceDetails.path]]/portal_config/display_options/agreements"
        data="{{agreements}}"
      ></katapult-firebase-worker>

      <katapult-color-picker-dialog></katapult-color-picker-dialog>

      <katapult-dialog-legacy id="iconPickerDialog" modal no-header overflow-hidden>
        <material-icon-picker slot="body"></material-icon-picker>
        <katapult-button slot="buttons" dialog-dismiss>Close</katapult-button>
      </katapult-dialog-legacy>

      <katapult-dialog-legacy id="addStatusDialog" max-width="500" draggable close-button on-closed="resetAddStatusDialog">
        <div slot="title">Add Status</div>
        <div slot="body">
          <paper-input id="addStatusNameInput" label="New Status Name"></paper-input>
          <div style="margin-top: 8px;">
            Status name must be unique within this app type (recommended to be globally unique, but not required).
          </div>
        </div>
        <katapult-button slot="buttons" color="var(--secondary-color)" on-click="addStatus">Add Status</katapult-button>
      </katapult-dialog-legacy>

      <katapult-dialog-legacy id="renameDeleteStatusDialog" max-width="500" no-cancel-on-outside-click no-cancel-on-esc-key>
        <div slot="title">[[titleCase(_dialogData.action)]] Status</div>
        <div slot="body" flex column style="gap: 8px;">
          <!-- Rename -->
          <template is="dom-if" if="[[equal(_dialogData.action, 'RENAME')]]">
            <div>Rename the <strong>[[_dialogData.oldStatusName]]</strong> status to:</div>
            <paper-input id="newStatusName" label="New status name" disabled="[[_dialogData.operationRunning]]"></paper-input>
            <div>
              <span>Renaming this status will update all non-archived <strong>[[_dialogData.appType]]</strong> apps with this status.</span>
              <span>Their status will be set to the new name, and no other status actions (including on-entry actions) will occur.</span>
            </div>
          </template>
          <!-- Delete -->
          <template is="dom-if" if="[[equal(_dialogData.action, 'DELETE')]]">
            <div>The <strong>[[_dialogData.oldStatusName]]</strong> status will be deleted, and apps in that status moved to:</div>
            <katapult-drop-down
              id="moveStatusName"
              label="Status to move to"
              items="[[_dialogData.otherStatuses]]"
              disabled="[[_dialogData.operationRunning]]"
            ></katapult-drop-down>
            <div>
              <span>Deleting this status will update all non-archived <strong>[[_dialogData.appType]]</strong> apps with this status.</span>
              <span
                >Their status will be set to the one specified above, and no other status actions (including on-entry actions) will
                occur.</span
              >
            </div>
          </template>

          <paper-checkbox id="includeArchived" style="padding: 8px;" disabled="[[_dialogData.operationRunning]]"
            >Also update archived apps</paper-checkbox
          >
          <div>
            <span>This can potentially be a long-running operation, and should be executed during off-peak hours.</span>
            <span>Any unsaved changes to this app type will be auto-saved.</span>
          </div>
        </div>
        <div slot="buttons" flex justify-space-between style="width: 100%;">
          <katapult-button dialog-dismiss outline disabled="[[_dialogData.operationRunning]]">Cancel</katapult-button>
          <template is="dom-if" if="[[equal(_dialogData.action, 'RENAME')]]">
            <katapult-button secondary-color loading="[[_dialogData.operationRunning]]" data-action="RENAME" on-click="renameDeleteStatus"
              >Update Status Name</katapult-button
            >
          </template>
          <template is="dom-if" if="[[equal(_dialogData.action, 'DELETE')]]">
            <katapult-button
              color="var(--paper-red-500)"
              loading="[[_dialogData.operationRunning]]"
              data-action="DELETE"
              on-click="renameDeleteStatus"
              >Delete [[_dialogData.oldStatusName]]</katapult-button
            >
          </template>
        </div>
      </katapult-dialog-legacy>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          step$="[[step.id]]"
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <!-- Utility Create Only -->
          <template is="dom-if" if="[[equal(step.id, 'utility_create_only')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox
                checked="[[_itemData.utility_create_only]]"
                data-path="_itemData.utility_create_only"
                on-change="toggleProperty"
              ></paper-checkbox>
            </paper-cell>
            <div buttons-container slot="current-step-body">
              <katapult-button
                color="var(--secondary-color)"
                data-path="_itemData.utility_create_only"
                data-value="false"
                data-parse="boolean"
                callback="updateProperty"
                confirm
                class="dialog-option"
                style="margin-right: 8px"
                >Yes</katapult-button
              >
              <katapult-button
                color="var(--secondary-color)"
                data-path="_itemData.utility_create_only"
                data-value="false"
                data-parse="boolean"
                callback="updateProperty"
                class="dialog-option"
                >No</katapult-button
              >
            </div>
          </template>

          <!-- Allowed Attachers Filter -->
          <template is="dom-if" if="[[equal(step.id, 'allowed_attachers_filter')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <k-logic-editor
              slot="body"
              block="[[_itemData.allowed_attachers_filter]]"
              schema="[[_klogicSchemas.allowedAttachersFilter]]"
              data-path="_itemData.allowed_attachers_filter"
              on-block-changed="updateProperty"
            ></k-logic-editor>
          </template>

          <!-- Statuses -->
          <template is="dom-if" if="[[equal(step.id, 'statuses')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <!-- Table -->
            <paper-table rounded outline slot="body" grow overflow-hidden>
              <paper-table-scroll data-path="_itemData.statuses" on-sort-changed="orderedItemsArraySortChanged">
                <!-- Header -->
                <paper-row grey short slot="before">
                  <paper-cell icon>
                    <material-icon icon="swap_vert"></material-icon>
                  </paper-cell>
                  <paper-cell>Status</paper-cell>
                  <paper-cell icon-button>
                    <paper-menu-button class="manage-status-button" horizontal-align="right" vertical-align="top" vertical-offset="48">
                      <katapult-button iconOnly noBorder slot="dropdown-trigger" icon-button icon="more_vert"></katapult-button>
                      <div slot="dropdown-content" style="border-radius: 12px; cursor: pointer;">
                        <div
                          flex
                          align-center
                          style="padding: 12px; gap: 8px; position: relative;"
                          data-copy-type="STATUS"
                          data-path="_itemData.statuses"
                          on-click="promptPaste"
                        >
                          <paper-ripple></paper-ripple>
                          <material-icon icon="content_paste"></material-icon>
                          <div>Paste</div>
                        </div>
                      </div>
                    </paper-menu-button>
                  </paper-cell>
                </paper-row>
                <!-- Body -->
                <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.statuses.*)]]" mutable-data>
                  <paper-header-content
                    data-path="_itemData.statuses.$(model.item.$key)"
                    on-opened-changed="updateProperty"
                    on-render-changed="updateProperty"
                    slot="sortable"
                    opened="[[item.$opened]]"
                    divider
                    no-click-row
                  >
                    <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                    <paper-cell slot="header" class="status-name">
                      <div>[[item.name]]</div>
                      <div class="active-indicator" hidden$="[[!item.active_for_engineering]]" title='App is "active" in this status'></div>
                      <div class="access-group-chips">
                        <div class="access-group-chip" hidden$="[[!item.applicant_can_write]]">
                          <katapult-icon icon="person" size="small" title="Applicant has write access on this status"></katapult-icon>
                        </div>
                        <div class="access-group-chip" hidden$="[[!item.engineering_contractor_can_write]]">
                          <katapult-icon
                            icon="architecture"
                            size="small"
                            title="Engineering contractor has write access on this status"
                          ></katapult-icon>
                        </div>
                        <div class="access-group-chip" hidden$="[[!item.data_collection_contractor_can_write]]">
                          <katapult-icon
                            icon="photo_camera"
                            size="small"
                            title="Data Collection contractor has write access on this status"
                          ></katapult-icon>
                        </div>
                        <div class="access-group-chip" hidden$="[[!item.review_contractor_can_write]]">
                          <katapult-icon
                            icon="engineering"
                            size="small"
                            title="Review contractor has write access on this status"
                          ></katapult-icon>
                        </div>
                      </div>
                    </paper-cell>
                    <paper-cell slot="header-end" icon-button>
                      <paper-menu-button class="manage-status-button" horizontal-align="right" vertical-align="top" vertical-offset="48">
                        <katapult-button iconOnly noBorder slot="dropdown-trigger" icon-button icon="more_vert"></katapult-button>
                        <div slot="dropdown-content" style="border-radius: 12px; cursor: pointer;">
                          <div
                            flex
                            align-center
                            style="padding: 12px; gap: 8px; position: relative;"
                            data-copy-type="STATUS"
                            data-path="_itemData.statuses.$(model.item.$key)"
                            on-click="promptCopy"
                          >
                            <paper-ripple></paper-ripple>
                            <material-icon icon="content_copy"></material-icon>
                            <div>Copy</div>
                          </div>
                          <template is="dom-if" if="[[showRenameDeleteStatusOptions(requiredStatuses, item.name)]]" restamp>
                            <div
                              flex
                              align-center
                              style="padding: 12px; gap: 8px; position: relative;"
                              data-action="RENAME"
                              on-click="promptManageStatus"
                            >
                              <paper-ripple></paper-ripple>
                              <material-icon icon="edit"></material-icon>
                              <div>Rename Status</div>
                            </div>
                            <div
                              flex
                              align-center
                              style="padding: 12px; gap: 8px; position: relative;"
                              data-action="DELETE"
                              on-click="promptManageStatus"
                            >
                              <paper-ripple></paper-ripple>
                              <material-icon icon="delete"></material-icon>
                              <div>Delete Status</div>
                            </div>
                          </template>
                        </div>
                      </paper-menu-button>
                    </paper-cell>
                    <template is="dom-if" if="[[item.$render]]" restamp>
                      <div slot="content">
                        <paper-table class="settings-table">
                          <!-- Entry Points -->
                          <paper-row>
                            <paper-cell icon>
                              <material-icon icon="hub"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Entry Points</paper-cell>
                            <paper-cell>
                              <paper-table white dividers rounded outline overflow-hidden>
                                <paper-row grey short>
                                  <paper-cell>Status</paper-cell>
                                  <paper-cell>Item</paper-cell>
                                </paper-row>
                                <template is="dom-repeat" items="[[getEntryPoints(item.$key, entryPointMap.*)]]">
                                  <template is="dom-if" if="[[!item.none]]">
                                    <paper-row expand>
                                      <paper-cell>
                                        <div class="chip">[[item.status]]</div>
                                      </paper-cell>
                                      <template is="dom-if" if="[[item.entryAction]]">
                                        <paper-cell>
                                          <div class="chip">
                                            <material-icon icon="login"></material-icon>
                                            <div>Entry Action</div>
                                          </div>
                                        </paper-cell>
                                      </template>
                                      <template is="dom-if" if="[[item.actionButton]]">
                                        <paper-cell>
                                          <katapult-button config="[[item.actionButton]]"></katapult-button>
                                        </paper-cell>
                                      </template>
                                    </paper-row>
                                  </template>
                                  <template is="dom-if" if="[[item.none]]">
                                    <paper-row>
                                      <paper-cell center>
                                        <div class="chip">None</div>
                                      </paper-cell>
                                    </paper-row>
                                  </template>
                                </template>
                              </paper-table>
                            </paper-cell>
                          </paper-row>
                          <!-- Write Access -->
                          <paper-row>
                            <paper-cell icon>
                              <material-icon icon="create"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Write Access</paper-cell>
                            <paper-cell>
                              <paper-table rounded outline grow overflow-hidden class="vertical-dividers" style="max-width: 600px;">
                                <paper-row grey short>
                                  <paper-cell center>Applicant</paper-cell>
                                  <paper-cell center>Engineering Contractor</paper-cell>
                                  <paper-cell center>Data Collection Contractor</paper-cell>
                                  <paper-cell center>Review Contractor</paper-cell>
                                </paper-row>
                                <paper-row>
                                  <paper-cell checkbox pointer center>
                                    <paper-checkbox
                                      checked="[[item.applicant_can_write]]"
                                      data-path="_itemData.statuses.$(model.item.$key).applicant_can_write"
                                      on-change="toggleProperty"
                                    ></paper-checkbox>
                                  </paper-cell>
                                  <paper-cell checkbox pointer center>
                                    <paper-checkbox
                                      checked="[[item.engineering_contractor_can_write]]"
                                      data-path="_itemData.statuses.$(model.item.$key).engineering_contractor_can_write"
                                      on-change="toggleProperty"
                                    ></paper-checkbox>
                                  </paper-cell>
                                  <paper-cell checkbox pointer center>
                                    <paper-checkbox
                                      checked="[[item.data_collection_contractor_can_write]]"
                                      data-path="_itemData.statuses.$(model.item.$key).data_collection_contractor_can_write"
                                      on-change="toggleProperty"
                                    ></paper-checkbox>
                                  </paper-cell>
                                  <paper-cell checkbox pointer center>
                                    <paper-checkbox
                                      checked="[[item.review_contractor_can_write]]"
                                      data-path="_itemData.statuses.$(model.item.$key).review_contractor_can_write"
                                      on-change="toggleProperty"
                                    ></paper-checkbox>
                                  </paper-cell>
                                </paper-row>
                              </paper-table>
                            </paper-cell>
                          </paper-row>
                          <!-- Application is Active -->
                          <paper-row>
                            <paper-cell icon>
                              <material-icon icon="toggle_on"></material-icon>
                            </paper-cell>
                            <paper-cell style="gap: var(--sl-spacing-medium);" shrink pointer checkbox>
                              <div>Active for Engineering</div>
                              <paper-checkbox
                                checked="[[item.active_for_engineering]]"
                                data-path="_itemData.statuses.$(model.item.$key).active_for_engineering"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell style="gap: var(--sl-spacing-medium);" shrink pointer checkbox>
                              <div>Active for Applicant</div>
                              <paper-checkbox
                                checked="[[item.active_for_applicant]]"
                                data-path="_itemData.statuses.$(model.item.$key).active_for_applicant"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                          </paper-row>
                          <!-- Color -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="palette"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Color</paper-cell>
                            <paper-cell>
                              <katapult-color-picker-button
                                color="[[item.color]]"
                                data-path="_itemData.statuses.$(model.item.$key).color"
                                on-change="updateProperty"
                              ></katapult-color-picker-button>
                            </paper-cell>
                          </paper-row>
                          <!-- On Entry -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="login"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Entry Actions</paper-cell>
                            <paper-cell>
                              <workflow-action-list
                                grow
                                actions="[[item.on_entry]]"
                                other-attributes="[[otherAttributes]]"
                                user-group="[[userGroup]]"
                                companies="[[companies]]"
                                timeline-models="[[timelineModels]]"
                                app-type="[[_itemData]]"
                                export-options="[[exportOptions]]"
                                data-path="_itemData.statuses.$(model.item.$key).on_entry"
                                on-copy="copyAction"
                                on-paste="promptPaste"
                                on-actions-changed="updateProperty"
                                model-space-details="[[modelSpaceDetails]]"
                              ></workflow-action-list>
                            </paper-cell>
                          </paper-row>
                          <!-- Action Buttons -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="bolt"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Action Buttons</paper-cell>
                            <paper-cell>
                              <!-- Table -->
                              <paper-table rounded white outline grow overflow-hidden>
                                <paper-table-scroll
                                  data-path="_itemData.statuses.$(model.item.$key).action_buttons"
                                  on-sort-changed="orderedItemsArraySortChanged"
                                >
                                  <!-- Header -->
                                  <paper-row grey short slot="before">
                                    <paper-cell icon>
                                      <material-icon icon="swap_vert"></material-icon>
                                    </paper-cell>
                                    <paper-cell>Button</paper-cell>
                                    <paper-cell icon-button>
                                      <paper-menu-button
                                        class="manage-status-button"
                                        horizontal-align="right"
                                        vertical-align="top"
                                        vertical-offset="48"
                                      >
                                        <katapult-button
                                          iconOnly
                                          noBorder
                                          slot="dropdown-trigger"
                                          icon-button
                                          icon="more_vert"
                                        ></katapult-button>
                                        <div slot="dropdown-content" style="border-radius: 12px; cursor: pointer;">
                                          <div
                                            flex
                                            align-center
                                            style="padding: 12px; gap: 8px; position: relative;"
                                            data-copy-type="ACTION_BUTTON"
                                            data-path="_itemData.statuses.$(model.item.$key).action_buttons"
                                            on-click="promptPaste"
                                          >
                                            <paper-ripple></paper-ripple>
                                            <material-icon icon="content_paste"></material-icon>
                                            <div>Paste</div>
                                          </div>
                                        </div>
                                      </paper-menu-button>
                                    </paper-cell>
                                  </paper-row>
                                  <!-- Body -->
                                  <template
                                    is="dom-repeat"
                                    items="[[getOrderedItemsArray(item.action_buttons.*)]]"
                                    as="button"
                                    mutable-data
                                  >
                                    <paper-header-content
                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key)"
                                      on-opened-changed="updateProperty"
                                      on-render-changed="updateProperty"
                                      slot="sortable"
                                      opened="[[button.$opened]]"
                                      divider
                                      no-click-row
                                    >
                                      <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                                      <paper-cell slot="header" style="gap: 16px;">
                                        <katapult-button icon="[[button.icon]]" color="[[button.color]]">[[button.label]]</katapult-button>
                                        <div class="access-group-chips">
                                          <div class="access-group-chip" hidden$="[[!button.for_applicant]]">
                                            <katapult-icon icon="person" size="small" title="Button is for the applicant"></katapult-icon>
                                          </div>
                                          <div class="access-group-chip" hidden$="[[!button.for_engineering_contractor]]">
                                            <katapult-icon
                                              icon="architecture"
                                              size="small"
                                              title="Button is for the engineering contractor"
                                            ></katapult-icon>
                                          </div>
                                          <div class="access-group-chip" hidden$="[[!button.for_data_collection_contractor]]">
                                            <katapult-icon
                                              icon="photo_camera"
                                              size="small"
                                              title="Button is for the data collection contractor"
                                            ></katapult-icon>
                                          </div>
                                          <div class="access-group-chip" hidden$="[[!button.for_review_contractor]]">
                                            <katapult-icon
                                              icon="engineering"
                                              size="small"
                                              title="Button is for the review contractor"
                                            ></katapult-icon>
                                          </div>
                                          <div class="access-group-chip" hidden$="[[!button.for_utility]]">
                                            <katapult-icon
                                              icon="power"
                                              size="small"
                                              title="Button is for the utility company"
                                            ></katapult-icon>
                                          </div>
                                        </div>
                                      </paper-cell>
                                      <paper-cell slot="header-end" icon-button>
                                        <paper-menu-button
                                          class="manage-status-button"
                                          horizontal-align="right"
                                          vertical-align="top"
                                          vertical-offset="48"
                                        >
                                          <katapult-button
                                            iconOnly
                                            noBorder
                                            slot="dropdown-trigger"
                                            icon-button
                                            icon="more_vert"
                                          ></katapult-button>
                                          <div slot="dropdown-content" style="border-radius: 12px; cursor: pointer;">
                                            <div
                                              flex
                                              align-center
                                              style="padding: 12px; gap: 8px; position: relative;"
                                              data-copy-type="ACTION_BUTTON"
                                              data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key)"
                                              on-click="promptCopy"
                                            >
                                              <paper-ripple></paper-ripple>
                                              <material-icon icon="content_copy"></material-icon>
                                              <div>Copy</div>
                                            </div>
                                            <div
                                              flex
                                              align-center
                                              style="padding: 12px; gap: 8px; position: relative;"
                                              data-copy-type="ACTION_BUTTON"
                                              data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key)"
                                              data-value="$null"
                                              on-click="updateProperty"
                                            >
                                              <paper-ripple></paper-ripple>
                                              <material-icon icon="delete"></material-icon>
                                              <div>Delete</div>
                                            </div>
                                          </div>
                                        </paper-menu-button>
                                      </paper-cell>
                                      <template is="dom-if" if="[[button.$render]]" restamp>
                                        <div slot="content">
                                          <!-- Label -->
                                          <paper-row input>
                                            <paper-cell icon>
                                              <material-icon icon="label"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Label</paper-cell>
                                            <paper-cell>
                                              <paper-input
                                                label="Label"
                                                value="[[button.label]]"
                                                data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).label"
                                                underline
                                                no-label-float
                                                on-value-changed="updateProperty"
                                              ></paper-input>
                                            </paper-cell>
                                          </paper-row>
                                          <!-- Color -->
                                          <paper-row expand>
                                            <paper-cell icon>
                                              <material-icon icon="palette"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Color</paper-cell>
                                            <paper-cell>
                                              <katapult-color-picker-button
                                                color="[[button.color]]"
                                                data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).color"
                                                on-change="updateProperty"
                                              ></katapult-color-picker-button>
                                            </paper-cell>
                                          </paper-row>
                                          <!-- Icon -->
                                          <paper-row expand>
                                            <paper-cell icon>
                                              <material-icon icon="emoji_objects"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Icon</paper-cell>
                                            <template is="dom-if" if="[[button.icon]]">
                                              <paper-cell icon>
                                                <material-icon icon="[[button.icon]]"></material-icon>
                                              </paper-cell>
                                            </template>
                                            <paper-cell buttons-container>
                                              <katapult-button
                                                icon="cached"
                                                size="16"
                                                on-click="pickAnIcon"
                                                data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).icon"
                                                style="margin-right: 8px;"
                                                >Change Icon</katapult-button
                                              >
                                              <template is="dom-if" if="[[button.icon]]">
                                                <katapult-button
                                                  icon="clear"
                                                  size="16"
                                                  on-click="updateProperty"
                                                  data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).icon"
                                                  data-value="$null"
                                                  >Clear Icon</katapult-button
                                                >
                                              </template>
                                            </paper-cell>
                                          </paper-row>
                                          <!-- Access Permission -->
                                          <paper-row expand>
                                            <paper-cell icon>
                                              <material-icon icon="person"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Access Permission</paper-cell>
                                            <paper-cell>
                                              <paper-table
                                                rounded
                                                outline
                                                grow
                                                overflow-hidden
                                                class="vertical-dividers"
                                                style="max-width: 800px;"
                                              >
                                                <paper-row grey short>
                                                  <paper-cell center>Applicant</paper-cell>
                                                  <paper-cell center>Engineering Contractor</paper-cell>
                                                  <paper-cell center>Data Collection Contractor</paper-cell>
                                                  <paper-cell center>Review Contractor</paper-cell>
                                                  <paper-cell center>Utility</paper-cell>
                                                </paper-row>
                                                <paper-row>
                                                  <paper-cell checkbox pointer center>
                                                    <paper-checkbox
                                                      checked="[[button.for_applicant]]"
                                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).for_applicant"
                                                      on-change="toggleProperty"
                                                    ></paper-checkbox>
                                                  </paper-cell>
                                                  <paper-cell checkbox pointer center>
                                                    <paper-checkbox
                                                      checked="[[button.for_engineering_contractor]]"
                                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).for_engineering_contractor"
                                                      on-change="toggleProperty"
                                                    ></paper-checkbox>
                                                  </paper-cell>
                                                  <paper-cell checkbox pointer center>
                                                    <paper-checkbox
                                                      checked="[[button.for_data_collection_contractor]]"
                                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).for_data_collection_contractor"
                                                      on-change="toggleProperty"
                                                    ></paper-checkbox>
                                                  </paper-cell>
                                                  <paper-cell checkbox pointer center>
                                                    <paper-checkbox
                                                      checked="[[button.for_review_contractor]]"
                                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).for_review_contractor"
                                                      on-change="toggleProperty"
                                                    ></paper-checkbox>
                                                  </paper-cell>
                                                  <paper-cell checkbox pointer center>
                                                    <paper-checkbox
                                                      checked="[[button.for_utility]]"
                                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).for_utility"
                                                      on-change="toggleProperty"
                                                    ></paper-checkbox>
                                                  </paper-cell>
                                                </paper-row>
                                              </paper-table>
                                            </paper-cell>
                                          </paper-row>
                                          <!-- Action -->
                                          <paper-row input>
                                            <paper-cell icon>
                                              <material-icon icon="wysiwyg"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Prompt Action</paper-cell>
                                            <paper-cell>
                                              <katapult-switcher
                                                items="[[buttonActions]]"
                                                value="[[button.action]]"
                                                value-path="value"
                                                label-path="label"
                                                data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).action"
                                                on-input="updateProperty"
                                              >
                                              </katapult-switcher>
                                            </paper-cell>
                                          </paper-row>
                                          <!-- If PROMPT_ACTION -->
                                          <template is="dom-if" if="[[equal(button.action, 'PROMPT_ACTION')]]" restamp>
                                            <!-- Confirmation Title -->
                                            <paper-row input>
                                              <paper-cell icon>
                                                <material-icon icon="title"></material-icon>
                                              </paper-cell>
                                              <paper-cell shrink>Confirmation Title</paper-cell>
                                              <paper-cell>
                                                <paper-input
                                                  label="Dialog Title"
                                                  value="[[button.confirmation_title]]"
                                                  data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).confirmation_title"
                                                  underline
                                                  no-label-float
                                                  on-value-changed="updateProperty"
                                                ></paper-input>
                                              </paper-cell>
                                            </paper-row>
                                            <!-- Confirmation Body -->
                                            <paper-row input expand>
                                              <paper-cell icon>
                                                <material-icon icon="subject"></material-icon>
                                              </paper-cell>
                                              <paper-cell shrink>Confirmation Body</paper-cell>
                                              <paper-cell>
                                                <paper-textarea
                                                  label="Dialog Body"
                                                  value="[[button.confirmation_body]]"
                                                  data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).confirmation_body"
                                                  underline
                                                  no-label-float
                                                  on-value-changed="updateProperty"
                                                ></paper-textarea>
                                              </paper-cell>
                                            </paper-row>
                                            <!-- Confirmation Button Label -->
                                            <paper-row input>
                                              <paper-cell icon>
                                                <material-icon icon="label"></material-icon>
                                              </paper-cell>
                                              <paper-cell shrink>Confirmation Button Label</paper-cell>
                                              <paper-cell>
                                                <paper-input
                                                  label="Button Label"
                                                  value="[[button.confirmation_button_label]]"
                                                  data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).confirmation_button_label"
                                                  underline
                                                  no-label-float
                                                  on-value-changed="updateProperty"
                                                ></paper-input>
                                              </paper-cell>
                                            </paper-row>
                                            <!-- Confirmation Prompts -->
                                            <paper-row expand>
                                              <paper-cell icon>
                                                <material-icon icon="description"></material-icon>
                                              </paper-cell>
                                              <paper-cell shrink>Confirmation Prompts</paper-cell>
                                              <paper-cell>
                                                <paper-table rounded outline grow overflow-hidden>
                                                  <paper-table-scroll
                                                    data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).confirmation_prompts"
                                                    on-sort-changed="orderedItemsArraySortChanged"
                                                  >
                                                    <!-- Header -->
                                                    <paper-row grey short slot="before">
                                                      <paper-cell icon>
                                                        <material-icon icon="swap_vert"></material-icon>
                                                      </paper-cell>
                                                      <paper-cell>Prompt Type</paper-cell>
                                                      <paper-cell>Attribute</paper-cell>
                                                      <paper-cell style="max-width: 90px;" justify-center>Required</paper-cell>
                                                      <paper-cell icon></paper-cell>
                                                      <paper-cell icon></paper-cell>
                                                    </paper-row>
                                                    <!-- Body -->
                                                    <template
                                                      is="dom-repeat"
                                                      items="[[getOrderedItemsArray(button.confirmation_prompts.*)]]"
                                                      mutable-data
                                                    >
                                                      <paper-header-content
                                                        data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key)"
                                                        on-opened-changed="updateProperty"
                                                        on-render-changed="updateProperty"
                                                        slot="sortable"
                                                        opened="[[item.$opened]]"
                                                        divider
                                                        no-click-row
                                                      >
                                                        <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                                                        <paper-cell slot="header" input>
                                                          <katapult-drop-down
                                                            label="Prompt Type"
                                                            value="[[item.type]]"
                                                            value-path="value"
                                                            label-path="label"
                                                            items="[[confirmationPromptTypes]]"
                                                            no-label-float
                                                            data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).type"
                                                            on-selected-changed="updateProperty"
                                                          >
                                                          </katapult-drop-down>
                                                        </paper-cell>
                                                        <paper-cell slot="header" input>
                                                          <katapult-drop-down
                                                            label="Attribute"
                                                            value="[[item.attribute]]"
                                                            value-path="$key"
                                                            label-path="$key"
                                                            label-function="[[camelCase]]"
                                                            items="[[getAttributesForPrompt(item.type, otherAttributes)]]"
                                                            no-label-float
                                                            filter="[[promptAttributeFilter]]"
                                                            data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).attribute"
                                                            on-selected-changed="updateProperty"
                                                          >
                                                          </katapult-drop-down>
                                                        </paper-cell>
                                                        <paper-cell style="max-width: 90px;" slot="header" icon>
                                                          <paper-checkbox
                                                            style="--primary-color: var(--paper-red-500);"
                                                            checked="[[item.required]]"
                                                            data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).required"
                                                            on-change="toggleProperty"
                                                          ></paper-checkbox>
                                                        </paper-cell>
                                                        <paper-cell slot="header-end" icon-button>
                                                          <katapult-button
                                                            iconOnly
                                                            noBorder
                                                            icon="delete"
                                                            data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key)"
                                                            data-value="$null"
                                                            on-click="updateProperty"
                                                          ></katapult-button>
                                                        </paper-cell>
                                                        <template is="dom-if" if="[[item.$render]]" restamp>
                                                          <div slot="content" flex column>
                                                            <!-- Required -->
                                                            <paper-row checkbox pointer>
                                                              <paper-cell icon>
                                                                <material-icon icon="emergency"></material-icon>
                                                              </paper-cell>
                                                              <paper-cell shrink>Required</paper-cell>
                                                              <paper-cell icon>
                                                                <paper-checkbox
                                                                  style="--primary-color: var(--paper-red-500);"
                                                                  checked="[[item.required]]"
                                                                  data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).required"
                                                                  on-change="toggleProperty"
                                                                ></paper-checkbox>
                                                              </paper-cell>
                                                              <paper-cell italic>The user must provide a response to continue</paper-cell>
                                                              <template is="dom-if" if="[[isDate(item)]]">
                                                                <paper-cell icon>
                                                                  <material-icon icon="today"></material-icon>
                                                                </paper-cell>
                                                                <paper-cell shrink>Restrict to past dates</paper-cell>
                                                                <paper-cell icon>
                                                                  <paper-checkbox
                                                                    style="--primary-color: var(--paper-red-500);"
                                                                    checked="[[item.before_today]]"
                                                                    data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).before_today"
                                                                    on-change="toggleProperty"
                                                                  ></paper-checkbox>
                                                                </paper-cell>
                                                                <paper-cell italic>Only allow dates before or equal to today</paper-cell>
                                                              </template>
                                                            </paper-row>
                                                            <!-- Update-Poles-Specific Controls -->
                                                            <template is="dom-if" if="[[equal(item.type, 'UPDATE_POLES')]]" restamp>
                                                              <paper-row checkbox pointer>
                                                                <paper-cell icon>
                                                                  <katapult-icon icon="checklist"></katapult-icon>
                                                                </paper-cell>
                                                                <paper-cell shrink>Required for All Poles</paper-cell>
                                                                <paper-cell icon>
                                                                  <paper-checkbox
                                                                    checked="[[item.required_for_all_poles]]"
                                                                    data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).required_for_all_poles"
                                                                    on-change="toggleProperty"
                                                                  ></paper-checkbox>
                                                                </paper-cell>
                                                                <paper-cell italic
                                                                  >The user must provide a response for every pole to continue (only applies
                                                                  if the prompt is required)</paper-cell
                                                                >
                                                              </paper-row>
                                                              <paper-row expand>
                                                                <paper-cell icon>
                                                                  <katapult-icon icon="filter_list"></katapult-icon>
                                                                </paper-cell>
                                                                <paper-cell shrink>Poles Filter</paper-cell>
                                                                <paper-cell>
                                                                  <k-logic-editor
                                                                    block="[[item.pole_filter]]"
                                                                    schema="[[_klogicSchemas.poleFilter]]"
                                                                    data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).pole_filter"
                                                                    on-block-changed="updateProperty"
                                                                  ></k-logic-editor>
                                                                </paper-cell>
                                                              </paper-row>
                                                            </template>
                                                            <!-- Default Value -->
                                                            <paper-row input expand>
                                                              <paper-cell icon>
                                                                <material-icon icon="input"></material-icon>
                                                              </paper-cell>
                                                              <paper-cell shrink>Default Value</paper-cell>
                                                              <paper-cell>
                                                                <input-element
                                                                  attribute_name="[[item.attribute]]"
                                                                  model="[[getSub(otherAttributes, item.attribute)]]"
                                                                  other-attributes="[[otherAttributes]]"
                                                                  user-group="[[userGroup]]"
                                                                  value="[[item.default_value]]"
                                                                  data-path="_itemData.statuses.$(model.parentModel.parentModel.item.$key).action_buttons.$(model.parentModel.button.$key).confirmation_prompts.$(model.item.$key).default_value"
                                                                  on-input-element-change="updateProperty"
                                                                  no-label-float
                                                                  grow
                                                                  hide-attribute-name
                                                                  job-creator="[[modelSpaceDetails.$key]]"
                                                                ></input-element>
                                                              </paper-cell>
                                                              <paper-cell italic
                                                                >Default Value will be pre-filled if there is no existing attribute
                                                                value.</paper-cell
                                                              >
                                                            </paper-row>
                                                          </div>
                                                        </template>
                                                      </paper-header-content>
                                                    </template>
                                                    <!-- Add Button -->
                                                    <paper-row
                                                      class="addButton"
                                                      ripple
                                                      light-grey
                                                      short
                                                      slot="after"
                                                      data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).confirmation_prompts"
                                                      on-click="addOrderedItem"
                                                    >
                                                      <paper-cell>
                                                        <material-icon icon="playlist_add"></material-icon>
                                                        <div>Add a Confirmation Prompt</div>
                                                      </paper-cell>
                                                    </paper-row>
                                                  </paper-table-scroll>
                                                </paper-table>
                                              </paper-cell>
                                            </paper-row>
                                          </template>
                                          <!-- If PROMPT_AGREEMENT -->
                                          <template is="dom-if" if="[[equal(button.action, 'PROMPT_AGREEMENT')]]" restamp>
                                            <!-- Agreement -->
                                            <paper-row input>
                                              <paper-cell icon>
                                                <material-icon icon="description"></material-icon>
                                              </paper-cell>
                                              <paper-cell shrink>Agreement</paper-cell>
                                              <paper-cell>
                                                <katapult-drop-down
                                                  label="Agreement"
                                                  items="[[_objectEntries(agreements)]]"
                                                  label-path="1.name"
                                                  value-path="0"
                                                  value="[[button.agreement]]"
                                                  data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).agreement"
                                                  underline
                                                  no-label-float
                                                  on-value-changed="updateProperty"
                                                ></katapult-drop-down>
                                              </paper-cell>
                                            </paper-row>
                                          </template>
                                          <!-- On Complete -->
                                          <paper-row expand>
                                            <paper-cell icon>
                                              <material-icon icon="pending_actions"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Complete Actions</paper-cell>
                                            <paper-cell>
                                              <workflow-action-list
                                                grow
                                                actions="[[button.on_complete]]"
                                                other-attributes="[[otherAttributes]]"
                                                user-group="[[userGroup]]"
                                                companies="[[companies]]"
                                                timeline-models="[[timelineModels]]"
                                                app-type="[[_itemData]]"
                                                export-options="[[exportOptions]]"
                                                data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).on_complete"
                                                on-actions-changed="updateProperty"
                                                model-space-details="[[modelSpaceDetails]]"
                                              ></workflow-action-list>
                                            </paper-cell>
                                          </paper-row>
                                          <!-- Hidden -->
                                          <paper-row expand>
                                            <paper-cell icon>
                                              <material-icon icon="visibility_off"></material-icon>
                                            </paper-cell>
                                            <paper-cell shrink>Hidden</paper-cell>
                                            <paper-cell shrink>
                                              <k-logic-editor
                                                block="[[button.hidden]]"
                                                schema="[[_klogicSchemas.buttonHidden]]"
                                                data-path="_itemData.statuses.$(model.parentModel.item.$key).action_buttons.$(model.button.$key).hidden"
                                                on-block-changed="updateProperty"
                                              ></k-logic-editor>
                                            </paper-cell>
                                            <paper-cell italic>When should this button be hidden?</paper-cell>
                                          </paper-row>
                                        </div>
                                      </template>
                                    </paper-header-content>
                                  </template>
                                  <!-- Add Button -->
                                  <paper-row
                                    class="addButton"
                                    ripple
                                    short
                                    slot="after"
                                    data-path="_itemData.statuses.$(model.item.$key).action_buttons"
                                    on-click="addOrderedItem"
                                  >
                                    <paper-cell>
                                      <material-icon icon="add_circle_outline"></material-icon>
                                      <div>Add a Button</div>
                                    </paper-cell>
                                  </paper-row>
                                </paper-table-scroll>
                              </paper-table>
                            </paper-cell>
                          </paper-row>
                        </paper-table>
                      </div>
                    </template>
                  </paper-header-content>
                </template>
                <!-- Add Button -->
                <paper-row class="addButton" ripple light-grey short slot="after" data-dialog-id="addStatusDialog" on-click="openDialog">
                  <paper-cell>
                    <material-icon icon="playlist_add"></material-icon>
                    <div>Add a Status</div>
                  </paper-cell>
                </paper-row>
              </paper-table-scroll>
            </paper-table>
          </template>

          <!-- Application Attributes -->
          <template is="dom-if" if="[[equal(step.id, 'app_attributes')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div flex column grow slot="body">
              <paper-table rounded outline grow overflow-hidden>
                <paper-table-scroll data-path="_itemData.app_attributes" on-sort-changed="orderedItemsArraySortChanged">
                  <!-- Header -->
                  <paper-row grey short slot="before">
                    <paper-cell icon>
                      <material-icon icon="swap_vert"></material-icon>
                    </paper-cell>
                    <paper-cell>Attribute</paper-cell>
                    <paper-cell style="max-width: 90px;" justify-center>Required</paper-cell>
                    <paper-cell style="max-width: 90px;" justify-center>Top Level</paper-cell>
                    <paper-cell icon></paper-cell>
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.app_attributes.*)]]" mutable-data>
                    <paper-header-content
                      data-path="_itemData.app_attributes.$(model.item.$key)"
                      on-opened-changed="updateProperty"
                      on-render-changed="updateProperty"
                      slot="sortable"
                      opened="[[item.$opened]]"
                      divider
                      no-click-row
                    >
                      <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                      <paper-cell slot="header" input>
                        <katapult-drop-down
                          label="Attribute"
                          value="[[item.attribute]]"
                          value-path="$key"
                          label-path="$key"
                          label-function="[[camelCase]]"
                          items="[[otherAttributes]]"
                          no-label-float
                          filter="[[_isJobAttribute]]"
                          data-path="_itemData.app_attributes.$(model.item.$key).attribute"
                          on-selected-changed="updateProperty"
                        >
                        </katapult-drop-down>
                      </paper-cell>
                      <paper-cell style="max-width: 90px;" slot="header" icon>
                        <paper-checkbox
                          style="--primary-color: var(--paper-red-500);"
                          checked="[[item.required]]"
                          data-path="_itemData.app_attributes.$(model.item.$key).required"
                          on-change="toggleProperty"
                        ></paper-checkbox>
                      </paper-cell>
                      <paper-cell style="max-width: 90px;" slot="header" icon>
                        <paper-checkbox
                          checked="[[item.top_level]]"
                          data-path="_itemData.app_attributes.$(model.item.$key).top_level"
                          on-change="toggleProperty"
                        ></paper-checkbox>
                      </paper-cell>
                      <paper-cell slot="header-end" icon-button>
                        <katapult-button
                          iconOnly
                          noBorder
                          icon="delete"
                          data-path="_itemData.app_attributes.$(model.item.$key)"
                          data-value="$null"
                          on-click="updateProperty"
                        ></katapult-button>
                      </paper-cell>
                      <template is="dom-if" if="[[item.$render]]" restamp>
                        <div class="settings-table" slot="content" flex column>
                          <!-- Required -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="emergency"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Required</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                style="--primary-color: var(--paper-red-500);"
                                checked="[[item.required]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).required"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                          </paper-row>
                          <!-- Top Level -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="label_important"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Top Level</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.top_level]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).top_level"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic
                              >Should this attribute always be visible or should it be displayed inside of the collapsible
                              region?</paper-cell
                            >
                          </paper-row>
                          <!-- Default Value -->
                          <paper-row input expand>
                            <paper-cell icon>
                              <material-icon icon="input"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Default Value</paper-cell>
                            <paper-cell>
                              <input-element
                                attribute_name="[[item.attribute]]"
                                model="[[getSub(otherAttributes, item.attribute)]]"
                                other-attributes="[[otherAttributes]]"
                                user-group="[[userGroup]]"
                                value="[[item.value]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).value"
                                on-input-element-change="updateProperty"
                                no-label-float
                                grow
                                hide-attribute-name
                                job-creator="[[modelSpaceDetails.$key]]"
                              >
                              </input-element>
                            </paper-cell>
                          </paper-row>
                          <!-- Hidden -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="visibility_off"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Hidden</paper-cell>
                            <paper-cell shrink>
                              <k-logic-editor
                                block="[[item.hidden]]"
                                schema="[[_klogicSchemas.appAttributeHidden]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).hidden"
                                on-block-changed="updateProperty"
                              ></k-logic-editor>
                            </paper-cell>
                            <paper-cell italic>When should this attribute be hidden?</paper-cell>
                          </paper-row>
                          <!-- Read Only -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="lock"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Read Only</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.utility_edit_only]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).utility_edit_only"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should this attribute only be editable by the utility company?</paper-cell>
                          </paper-row>
                          <!-- Show Attribute Name -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="label"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Show Attribute Name</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.show_attribute_name]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).show_attribute_name"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should this attribute's name be shown alongside it's input field?</paper-cell>
                          </paper-row>
                          <!-- Confirm Change -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="domain_verification"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Confirm Attribute Changes</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.confirm_change]]"
                                data-path="_itemData.app_attributes.$(model.item.$key).confirm_change"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Show a confirmation dialog anytime this attribute is changed?</paper-cell>
                          </paper-row>
                        </div>
                      </template>
                    </paper-header-content>
                  </template>
                  <!-- Add Button -->
                  <paper-row
                    class="addButton"
                    ripple
                    light-grey
                    short
                    slot="after"
                    data-path="_itemData.app_attributes"
                    on-click="addOrderedItem"
                  >
                    <paper-cell>
                      <material-icon icon="playlist_add"></material-icon>
                      <div>Add an Application Attribute</div>
                    </paper-cell>
                  </paper-row>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>

          <!-- Pole Attributes -->
          <template is="dom-if" if="[[equal(step.id, 'pole_attributes')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div flex column grow slot="body">
              <paper-table rounded outline grow overflow-hidden>
                <paper-table-scroll data-path="_itemData.pole_attributes" on-sort-changed="orderedItemsArraySortChanged">
                  <!-- Header -->
                  <paper-row grey short slot="before">
                    <paper-cell icon>
                      <material-icon icon="swap_vert"></material-icon>
                    </paper-cell>
                    <paper-cell>Attribute</paper-cell>
                    <paper-cell style="max-width: 90px;" justify-center>Required</paper-cell>
                    <paper-cell style="max-width: 90px;" justify-center>Top Level</paper-cell>
                    <paper-cell icon></paper-cell>
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.pole_attributes.*)]]" mutable-data>
                    <paper-header-content
                      data-path="_itemData.pole_attributes.$(model.item.$key)"
                      on-opened-changed="updateProperty"
                      on-render-changed="updateProperty"
                      slot="sortable"
                      opened="[[item.$opened]]"
                      divider
                      no-click-row
                    >
                      <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                      <paper-cell slot="header" input>
                        <katapult-drop-down
                          label="Attribute"
                          value="[[item.attribute]]"
                          value-path="$key"
                          label-path="$key"
                          label-function="[[camelCase]]"
                          items="[[otherAttributes]]"
                          no-label-float
                          data-path="_itemData.pole_attributes.$(model.item.$key).attribute"
                          on-selected-changed="updateProperty"
                        >
                        </katapult-drop-down>
                      </paper-cell>
                      <paper-cell style="max-width: 90px;" slot="header" icon>
                        <paper-checkbox
                          style="--primary-color: var(--paper-red-500);"
                          checked="[[item.required]]"
                          data-path="_itemData.pole_attributes.$(model.item.$key).required"
                          on-change="toggleProperty"
                        ></paper-checkbox>
                      </paper-cell>
                      <paper-cell style="max-width: 90px;" slot="header" icon>
                        <paper-checkbox
                          checked="[[item.top_level]]"
                          data-path="_itemData.pole_attributes.$(model.item.$key).top_level"
                          on-change="toggleProperty"
                        ></paper-checkbox>
                      </paper-cell>
                      <paper-cell slot="header-end" icon-button>
                        <katapult-button
                          iconOnly
                          noBorder
                          icon="delete"
                          data-path="_itemData.pole_attributes.$(model.item.$key)"
                          data-value="$null"
                          on-click="updateProperty"
                        ></katapult-button>
                      </paper-cell>
                      <template is="dom-if" if="[[item.$render]]" restamp>
                        <div class="settings-table" slot="content" flex column>
                          <!-- Required -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="emergency"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Required</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                style="--primary-color: var(--paper-red-500);"
                                checked="[[item.required]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).required"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                          </paper-row>
                          <!-- Top Level -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="label_important"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Top Level</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.top_level]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).top_level"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic
                              >Should this attribute always be visible or should it be displayed inside of the collapsible
                              region?</paper-cell
                            >
                          </paper-row>
                          <!-- Display Shortname -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="short_text"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Abbreviation</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.display_shortname]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).display_shortname"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should this attribute's abbreviation be used instead?</paper-cell>
                          </paper-row>
                          <!-- Label -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="label"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Label</paper-cell>
                            <paper-cell>
                              <paper-input
                                label="Label"
                                value="[[item.label]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).label"
                                underline
                                no-label-float
                                on-value-changed="updateProperty"
                              ></paper-input>
                            </paper-cell>
                            <paper-cell italic>An optional label to display in place of the default</paper-cell>
                          </paper-row>
                          <!-- Attribute Subpath -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="subdirectory_arrow_right"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Attribute Subpath</paper-cell>
                            <paper-cell>
                              <paper-input
                                label="Subpath"
                                value="[[item.attribute_subpath]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).attribute_subpath"
                                underline
                                no-label-float
                                on-value-changed="updateProperty"
                              ></paper-input>
                            </paper-cell>
                            <paper-cell italic>A sub-property of the attribute to display if the attribute is complex</paper-cell>
                          </paper-row>
                          <!-- Display Attribute -->
                          <template is="dom-if" if="[[item.attribute_subpath]]">
                            <paper-row input>
                              <paper-cell icon>
                                <material-icon icon="list"></material-icon>
                              </paper-cell>
                              <paper-cell shrink>Subpath Attribute</paper-cell>
                              <paper-cell>
                                <katapult-drop-down
                                  label="Subpath Attribute"
                                  value="[[item.display_attribute]]"
                                  value-path="$key"
                                  label-path="$key"
                                  label-function="[[camelCase]]"
                                  items="[[otherAttributes]]"
                                  no-label-float
                                  data-path="_itemData.pole_attributes.$(model.item.$key).display_attribute"
                                  on-selected-changed="updateProperty"
                                >
                                </katapult-drop-down>
                              </paper-cell>
                              <paper-cell italic>An attribute to use to display the sub-property of this attribute</paper-cell>
                            </paper-row>
                          </template>
                          <!-- Stamp -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="approval"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Allow Overwrite</paper-cell>
                            <paper-cell shrink>
                              <paper-checkbox
                                checked="[[item.allow_overwrite]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).allow_overwrite"
                                on-checked-changed="updateProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic
                              >Should this attribute overwrite existing values? (Default behavior will only write if the value has not
                              already been set.)</paper-cell
                            >
                          </paper-row>
                          <!-- Value -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="functions"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Value</paper-cell>
                            <paper-cell shrink>
                              <k-logic-editor
                                block="[[item.value]]"
                                schema="[[_klogicSchemas.poleAttributeValue]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).value"
                                on-block-changed="updateProperty"
                              ></k-logic-editor>
                            </paper-cell>
                          </paper-row>
                          <!-- Hidden -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="visibility_off"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Hidden</paper-cell>
                            <paper-cell shrink>
                              <k-logic-editor
                                block="[[item.hidden]]"
                                schema="[[_klogicSchemas.poleAttributeHidden]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).hidden"
                                on-block-changed="updateProperty"
                              ></k-logic-editor>
                            </paper-cell>
                            <paper-cell italic>When should this attribute be hidden?</paper-cell>
                          </paper-row>
                          <!-- Read Only -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="lock"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Read Only</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.read_only]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).read_only"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should this attribute only be editable by the utility company?</paper-cell>
                          </paper-row>
                          <!-- Confirm Change -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="domain_verification"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Confirm Attribute Changes</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.confirm_change]]"
                                data-path="_itemData.pole_attributes.$(model.item.$key).confirm_change"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Show a confirmation dialog anytime this attribute is changed?</paper-cell>
                          </paper-row>
                        </div>
                      </template>
                    </paper-header-content>
                  </template>
                  <!-- Add Button -->
                  <paper-row
                    class="addButton"
                    ripple
                    light-grey
                    short
                    slot="after"
                    data-path="_itemData.pole_attributes"
                    on-click="addOrderedItem"
                  >
                    <paper-cell>
                      <material-icon icon="playlist_add"></material-icon>
                      <div>Add a Pole Attribute</div>
                    </paper-cell>
                  </paper-row>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>

          <!-- Application Attachment Types -->
          <template is="dom-if" if="[[equal(step.id, 'app_attach_types')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div flex column grow slot="body">
              <paper-table rounded outline grow overflow-hidden>
                <paper-table-scroll data-path="_itemData.app_attach_types" on-sort-changed="orderedItemsArraySortChanged">
                  <!-- Header -->
                  <paper-row grey short slot="before">
                    <paper-cell icon>
                      <material-icon icon="swap_vert"></material-icon>
                    </paper-cell>
                    <paper-cell>Attachment Type</paper-cell>
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.app_attach_types.*)]]" mutable-data>
                    <paper-header-content
                      data-path="_itemData.app_attach_types.$(model.item.$key)"
                      on-opened-changed="updateProperty"
                      on-render-changed="updateProperty"
                      slot="sortable"
                      opened="[[item.$opened]]"
                      divider
                      no-click-row
                    >
                      <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                      <paper-cell slot="header" input>
                        <paper-input
                          label="Attachment Type"
                          value="[[item.name]]"
                          data-path="_itemData.app_attach_types.$(model.item.$key).name"
                          no-label-float
                          on-value-changed="updateProperty"
                        ></paper-input>
                      </paper-cell>
                      <paper-cell slot="header-end" icon-button>
                        <katapult-button
                          iconOnly
                          noBorder
                          icon="delete"
                          data-path="_itemData.app_attach_types.$(model.item.$key)"
                          data-value="$null"
                          on-click="updateProperty"
                        ></katapult-button>
                      </paper-cell>
                      <template is="dom-if" if="[[item.$render]]" restamp>
                        <div class="settings-table" slot="content" flex column>
                          <!-- Max Poles -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="tag"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Max Poles</paper-cell>
                            <paper-cell class="timerLength">
                              <paper-input
                                label="Max Poles"
                                type="number"
                                min="0"
                                max="999999"
                                value="[[item.max_poles]]"
                                data-path="_itemData.app_attach_types.$(model.item.$key).max_poles"
                                underline
                                no-label-float
                                on-value-changed="updateProperty"
                              ></paper-input>
                            </paper-cell>
                            <paper-cell italic>The maximum number of poles allowed for applications with this attachment type</paper-cell>
                          </paper-row>
                          <!-- Attachments -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="list"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Attachments</paper-cell>
                            <paper-cell>
                              <!-- Table -->
                              <paper-table rounded white outline dividers grow overflow-hidden>
                                <paper-table-scroll
                                  data-path="_itemData.app_attach_types.$(model.item.$key).attachments"
                                  on-sort-changed="orderedItemsArraySortChanged"
                                >
                                  <!-- Header -->
                                  <paper-row grey short slot="before">
                                    <paper-cell icon>
                                      <material-icon icon="swap_vert"></material-icon>
                                    </paper-cell>
                                    <paper-cell>Attachment</paper-cell>
                                    <paper-cell class="timerLength" text-center justify-center>Engineering Required</paper-cell>
                                    <paper-cell class="timerLength" text-center justify-center>Contributes to Pole Count</paper-cell>
                                    <paper-cell class="timerLength" text-center justify-center>Selected by Default</paper-cell>
                                    <paper-cell icon></paper-cell>
                                  </paper-row>
                                  <!-- Body -->
                                  <template is="dom-repeat" items="[[getOrderedItemsArray(item.attachments.*)]]" mutable-data>
                                    <paper-row slot="sortable">
                                      <paper-cell icon drag-handle>[[add(index, 1)]]</paper-cell>
                                      <paper-cell>
                                        <paper-input
                                          label="Attachment"
                                          value="[[item.name]]"
                                          data-path="_itemData.app_attach_types.$(model.parentModel.item.$key).attachments.$(model.item.$key).name"
                                          no-label-float
                                          on-value-changed="updateProperty"
                                        ></paper-input>
                                      </paper-cell>
                                      <paper-cell class="timerLength" justify-center>
                                        <paper-checkbox
                                          checked="[[item.engineering_required]]"
                                          data-path="_itemData.app_attach_types.$(model.parentModel.item.$key).attachments.$(model.item.$key).engineering_required"
                                          on-change="toggleProperty"
                                        ></paper-checkbox>
                                      </paper-cell>
                                      <paper-cell class="timerLength" justify-center>
                                        <paper-checkbox
                                          checked="[[item.pole_counts]]"
                                          data-path="_itemData.app_attach_types.$(model.parentModel.item.$key).attachments.$(model.item.$key).pole_counts"
                                          on-change="toggleProperty"
                                        ></paper-checkbox>
                                      </paper-cell>
                                      <paper-cell class="timerLength" justify-center>
                                        <paper-checkbox
                                          checked="[[item.is_default]]"
                                          data-path="_itemData.app_attach_types.$(model.parentModel.item.$key).attachments.$(model.item.$key).is_default"
                                          on-change="toggleProperty"
                                        ></paper-checkbox>
                                      </paper-cell>
                                      <paper-cell icon-button>
                                        <katapult-button
                                          iconOnly
                                          noBorder
                                          icon="delete"
                                          data-path="_itemData.app_attach_types.$(model.parentModel.item.$key).attachments.$(model.item.$key)"
                                          data-value="$null"
                                          on-click="updateProperty"
                                        ></katapult-button>
                                      </paper-cell>
                                    </paper-row>
                                  </template>
                                  <!-- Add Button -->
                                  <paper-row
                                    class="addButton"
                                    ripple
                                    short
                                    slot="after"
                                    data-path="_itemData.app_attach_types.$(model.item.$key).attachments"
                                    on-click="addOrderedItem"
                                  >
                                    <paper-cell>
                                      <material-icon icon="playlist_add"></material-icon>
                                      <div>Add an Attachment</div>
                                    </paper-cell>
                                  </paper-row>
                                </paper-table-scroll>
                              </paper-table>
                            </paper-cell>
                          </paper-row>
                          <!-- Allowed Attachers Filter -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="person"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Allowed Attachers</paper-cell>
                            <paper-cell shrink>
                              <k-logic-editor
                                block="[[item.allowed_attachers_filter]]"
                                schema="[[_klogicSchemas.allowedAttachersFilter]]"
                                data-path="_itemData.app_attach_types.$(model.item.$key).allowed_attachers_filter"
                                on-block-changed="updateProperty"
                              ></k-logic-editor>
                            </paper-cell>
                          </paper-row>
                        </div>
                      </template>
                    </paper-header-content>
                  </template>
                  <!-- Add Button -->
                  <paper-row
                    class="addButton"
                    ripple
                    light-grey
                    short
                    slot="after"
                    data-path="_itemData.app_attach_types"
                    on-click="addOrderedItem"
                  >
                    <paper-cell>
                      <material-icon icon="playlist_add"></material-icon>
                      <div>Add an Attachment Type</div>
                    </paper-cell>
                  </paper-row>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>

          <!-- Timers -->
          <template is="dom-if" if="[[equal(step.id, 'timers')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div flex column grow slot="body">
              <paper-table rounded outline grow overflow-hidden>
                <paper-table-scroll data-path="_itemData.timers" on-sort-changed="orderedItemsArraySortChanged">
                  <!-- Header -->
                  <paper-row grey short slot="before">
                    <paper-cell icon>
                      <material-icon icon="swap_vert"></material-icon>
                    </paper-cell>
                    <paper-cell>Name</paper-cell>
                    <!-- <paper-cell style="max-width: 120px">Length</paper-cell>
                    <paper-cell></paper-cell> -->
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.timers.*)]]" mutable-data>
                    <paper-header-content
                      data-path="_itemData.timers.$(model.item.$key)"
                      on-opened-changed="updateProperty"
                      on-render-changed="updateProperty"
                      slot="sortable"
                      opened="[[item.$opened]]"
                      divider
                      no-click-row
                    >
                      <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                      <paper-cell slot="header" input>
                        <paper-input
                          label="Name"
                          value="[[item.label]]"
                          data-path="_itemData.timers.$(model.item.$key).label"
                          no-label-float
                          on-value-changed="updateProperty"
                        ></paper-input>
                      </paper-cell>
                      <!-- <paper-cell class="timerLength" slot="header">
                        <paper-input type="number" label="Length" value="[[item.length]]" data-path="_itemData.timers.$(model.item.$key).length" no-label-float on-value-changed="updateProperty">
                          <span slot="suffix">days</span>
                        </paper-input>
                      </paper-cell>
                      <paper-cell slot="header"></paper-cell> -->
                      <paper-cell slot="header-end" icon-button>
                        <katapult-button
                          iconOnly
                          noBorder
                          icon="delete"
                          data-path="_itemData.timers.$(model.item.$key)"
                          data-value="$null"
                          on-click="updateProperty"
                        ></katapult-button>
                      </paper-cell>
                      <template is="dom-if" if="[[item.$render]]" restamp>
                        <div class="settings-table" slot="content" flex column>
                          <!-- Description -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="description"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Description</paper-cell>
                            <paper-cell>
                              <paper-input
                                label="Description"
                                value="[[item.description]]"
                                data-path="_itemData.timers.$(model.item.$key).description"
                                underline
                                no-label-float
                                on-value-changed="updateProperty"
                              ></paper-input>
                            </paper-cell>
                          </paper-row>
                          <!-- Length -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="date_range"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Length</paper-cell>
                            <paper-cell class="timerLength">
                              <paper-input
                                type="number"
                                label="Length"
                                value="[[item.length]]"
                                data-path="_itemData.timers.$(model.item.$key).length"
                                no-label-float
                                underline
                                on-value-changed="updateProperty"
                              >
                                <span slot="suffix">days</span>
                              </paper-input>
                            </paper-cell>
                          </paper-row>
                          <!-- Allow Utility Adjustment -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="engineering"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Allow Utility Adjustment</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.allow_utility_adjustment]]"
                                data-path="_itemData.timers.$(model.item.$key).allow_utility_adjustment"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should the utility company be allowed to change the timer's length?</paper-cell>
                          </paper-row>
                          <!-- Allow Extension Request -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="more_time"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Allow Extension Request</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.allow_extension_request]]"
                                data-path="_itemData.timers.$(model.item.$key).allow_extension_request"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should the applicant be allowed to request more time?</paper-cell>
                          </paper-row>
                          <!-- Allow Applicant Manual Timeout -->
                          <paper-row checkbox pointer>
                            <paper-cell icon>
                              <material-icon icon="person"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Allow Manual Timeout</paper-cell>
                            <paper-cell icon>
                              <paper-checkbox
                                checked="[[item.allow_applicant_manual_timeout]]"
                                data-path="_itemData.timers.$(model.item.$key).allow_applicant_manual_timeout"
                                on-change="toggleProperty"
                              ></paper-checkbox>
                            </paper-cell>
                            <paper-cell italic>Should the applicant be allowed to manually take action?</paper-cell>
                          </paper-row>
                          <!-- On Timeout -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="pending_actions"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Timeout Actions</paper-cell>
                            <paper-cell>
                              <workflow-action-list
                                grow
                                actions="[[item.on_timeout]]"
                                other-attributes="[[otherAttributes]]"
                                user-group="[[userGroup]]"
                                companies="[[companies]]"
                                timeline-models="[[timelineModels]]"
                                app-type="[[_itemData]]"
                                export-options="[[exportOptions]]"
                                data-path="_itemData.timers.$(model.item.$key).on_timeout"
                                on-actions-changed="updateProperty"
                                model-space-details="[[modelSpaceDetails]]"
                              ></workflow-action-list>
                            </paper-cell>
                          </paper-row>
                        </div>
                      </template>
                    </paper-header-content>
                  </template>
                  <!-- Add Button -->
                  <paper-row class="addButton" ripple light-grey short slot="after" data-path="_itemData.timers" on-click="addOrderedItem">
                    <paper-cell>
                      <material-icon icon="alarm_add"></material-icon>
                      <div>Add a Timer</div>
                    </paper-cell>
                  </paper-row>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>

          <!-- Warnings -->
          <template is="dom-if" if="[[equal(step.id, 'warnings')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div flex column grow slot="body">
              <paper-table rounded outline grow overflow-hidden>
                <paper-table-scroll data-path="_itemData.warnings" on-sort-changed="orderedItemsArraySortChanged">
                  <!-- Header -->
                  <paper-row grey short slot="before">
                    <paper-cell icon>
                      <material-icon icon="swap_vert"></material-icon>
                    </paper-cell>
                    <paper-cell>Name</paper-cell>
                    <paper-cell icon></paper-cell>
                  </paper-row>
                  <!-- Body -->
                  <template is="dom-repeat" items="[[getOrderedItemsArray(_itemData.warnings.*)]]" mutable-data>
                    <paper-header-content
                      data-path="_itemData.warnings.$(model.item.$key)"
                      on-opened-changed="updateProperty"
                      on-render-changed="updateProperty"
                      slot="sortable"
                      opened="[[item.$opened]]"
                      divider
                      no-click-row
                    >
                      <paper-cell slot="header" icon drag-handle>[[add(index, 1)]]</paper-cell>
                      <paper-cell slot="header" input>
                        <paper-input
                          label="Name"
                          value="[[item.label]]"
                          data-path="_itemData.warnings.$(model.item.$key).label"
                          no-label-float
                          on-value-changed="updateProperty"
                        ></paper-input>
                      </paper-cell>
                      <!-- <paper-cell class="timerLength" slot="header">
                        <paper-input type="number" label="Length" value="[[item.length]]" data-path="_itemData.warnings.$(model.item.$key).length" no-label-float on-value-changed="updateProperty">
                          <span slot="suffix">days</span>
                        </paper-input>
                      </paper-cell>
                      <paper-cell slot="header"></paper-cell> -->
                      <paper-cell slot="header-end" icon-button>
                        <katapult-button
                          iconOnly
                          noBorder
                          icon="delete"
                          data-path="_itemData.warnings.$(model.item.$key)"
                          data-value="$null"
                          on-click="updateProperty"
                        ></katapult-button>
                      </paper-cell>
                      <template is="dom-if" if="[[item.$render]]" restamp>
                        <div class="settings-table" slot="content" flex column>
                          <!-- Description -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="description"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Description</paper-cell>
                            <paper-cell>
                              <paper-input
                                label="Description"
                                value="[[item.description]]"
                                data-path="_itemData.warnings.$(model.item.$key).description"
                                underline
                                no-label-float
                                on-value-changed="updateProperty"
                              ></paper-input>
                            </paper-cell>
                          </paper-row>
                          <!-- Applies To -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="category"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Applies To</paper-cell>
                            <paper-cell>
                              <katapult-switcher
                                items="[[warningAppliesToList]]"
                                value="[[item.applies_to]]"
                                value-path="$val"
                                label-path="$label"
                                data-path="_itemData.warnings.$(model.item.$key).applies_to"
                                on-input="updateProperty"
                              >
                              </katapult-switcher>
                            </paper-cell>
                          </paper-row>
                          <!-- Level -->
                          <paper-row input>
                            <paper-cell icon>
                              <material-icon icon="error"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Severity</paper-cell>
                            <paper-cell>
                              <katapult-switcher
                                class$="[[item.level]]"
                                items="[[warningLevelsList]]"
                                value="[[item.level]]"
                                value-path="."
                                label-path="."
                                label-function="[[camelLowerCase]]"
                                data-path="_itemData.warnings.$(model.item.$key).level"
                                on-input="updateProperty"
                              >
                              </katapult-switcher>
                            </paper-cell>
                          </paper-row>
                          <!-- Hidden -->
                          <paper-row expand>
                            <paper-cell icon>
                              <material-icon icon="help"></material-icon>
                            </paper-cell>
                            <paper-cell shrink>Condition</paper-cell>
                            <paper-cell shrink>
                              <k-logic-editor
                                block="[[item.applies]]"
                                schema="[[_getSchemaForWarning(item.applies_to)]]"
                                data-path="_itemData.warnings.$(model.item.$key).applies"
                                on-block-changed="updateProperty"
                              ></k-logic-editor>
                            </paper-cell>
                            <paper-cell italic>When does this warning apply?</paper-cell>
                          </paper-row>
                        </div>
                      </template>
                    </paper-header-content>
                  </template>
                  <!-- Add Button -->
                  <paper-row
                    class="addButton"
                    ripple
                    light-grey
                    short
                    slot="after"
                    data-path="_itemData.warnings"
                    data-type="warning"
                    on-click="addOrderedItem"
                  >
                    <paper-cell>
                      <material-icon icon="add_circle_outline"></material-icon>
                      <div>Add a Warning</div>
                    </paper-cell>
                  </paper-row>
                </paper-table-scroll>
              </paper-table>
            </div>
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      ...super.properties,
      isPortalAdmin: {
        type: Boolean,
        value: false
      },
      warningAppliesToList: {
        type: Array,
        value: () => [
          { $val: 'APP', $label: 'Application' },
          { $val: 'POLES', $label: 'Poles' }
        ]
      },
      warningLevelsList: {
        type: Array,
        value: () => ['INFO', 'WARNING', 'ERROR']
      },
      requiredStatuses: {
        type: Array
      }
    };
  }

  static get observers() {
    return ['_computeIsPortalAdmin(userGroup)'];
  }

  constructor() {
    super();
    this.requiredStatuses = ['Draft', 'Canceled'];
    this.buttonActions = [
      { value: 'PROMPT_ACTION', label: 'Show Confirmation Dialog' },
      { value: 'PROMPT_AGREEMENT', label: 'Link To Agreement' },
      { value: 'DESELECT_POLES', label: 'Show Deselect Poles Dialog (LEGACY)' }
    ];
    this.confirmationPromptTypes = [
      { value: 'UPDATE_APPLICATION', label: 'Update Job Metadata' },
      { value: 'UPDATE_POLES', label: 'Update Node Attribute' }
    ];

    this.camelLowerCase = (x = '') => CamelCase(x?.toLowerCase());
    this.titleCase = (x = '') =>
      x.length > 0
        ? x
            .split(' ')
            .map((x) => x[0].toUpperCase() + x.slice(1).toLowerCase())
            .join(' ')
        : '';
    this._objectEntries = (obj = {}) => Object.entries(obj);
    this._isJobAttribute = (x) => x.attribute_types?.includes?.('job');
    this._isNodeAttribute = (x) => x.attribute_types?.includes?.('node');
    this._getSchemaForWarning = (appliesTo) =>
      appliesTo === 'POLES' ? { node: DEFAULT_SCHEMAS.NODE, ...this._klogicSchemas.warningApplies } : this._klogicSchemas.warningApplies;

    this._klogicSchemas = {
      allowedAttachersFilter: PORTAL_SCHEMAS.ATTACHER_DATA,
      buttonHidden: {
        metadata: DEFAULT_SCHEMAS.JOB.metadata,
        nodes: DEFAULT_SCHEMAS.JOB.nodes,
        ...PORTAL_SCHEMAS.ATTACHER_DATA
      },
      appAttributeHidden: {
        metadata: DEFAULT_SCHEMAS.JOB.metadata,
        ...PORTAL_SCHEMAS.ATTACHER_DATA
      },
      poleAttributeValue: PORTAL_SCHEMAS.POLE_LOOKUP_ENTRY,
      poleAttributeHidden: {
        metadata: DEFAULT_SCHEMAS.JOB.metadata,
        node: DEFAULT_SCHEMAS.NODE,
        ...PORTAL_SCHEMAS.ATTACHER_DATA
      },
      warningApplies: {
        metadata: DEFAULT_SCHEMAS.JOB.metadata,
        nodes: DEFAULT_SCHEMAS.JOB.nodes,
        files: 'object',
        appAttachType: 'object',
        lookupIdsInOtherActiveApps: 'list',
        lookupIdsInOwnActiveApps: 'list',
        submissionEntryDetails: PORTAL_SCHEMAS.SUBMISSION_ENTRY_DETAILS,
        existingTakeOffAttachments: 'list',
        attacher: { name: 'text' },
        attacherLabels: 'list'
      },
      poleFilter: {
        pole: DEFAULT_SCHEMAS.NODE,
        nodes: DEFAULT_SCHEMAS.JOB.nodes,
        metadata: DEFAULT_SCHEMAS.JOB.metadata
      }
    };
  }

  ready() {
    super.ready();

    this.keyMatchLabel = true;

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'utility_create_only',
        title: 'Only allow the utility to create this type of application',
        trigger: () => this.get('_itemData.utility_create_only') != null
      },
      {
        id: 'allowed_attachers_filter',
        title: 'Allowed Attachers Filter'
      },
      {
        id: 'statuses',
        title: 'Application Statuses'
      },
      {
        id: 'app_attributes',
        title: 'Application Attributes'
      },
      {
        id: 'pole_attributes',
        title: 'Pole Attributes'
      },
      {
        id: 'app_attach_types',
        title: 'Attachment Types'
        // }, {
        //   id: 'displayed_dates',
        //   title: 'Displayed Dates'
      },
      {
        id: 'timers',
        title: 'Timers'
      },
      {
        id: 'warnings',
        title: 'Warnings'
      }
    ]);
    this.skipCreationSteps = true;
  }

  async beforeSave() {
    const cancelSaveProcess = await confirmSyncPortalConfig(this.modelSpaceDetails.$key);
    if (cancelSaveProcess) throw new Error('User did not confirm sync portal config');
  }

  async afterSave() {
    await syncPortalConfigs(this.modelSpaceDetails.$key);
  }

  _itemDataChanged(changeRecord) {
    // Call parent class method.
    super._itemDataChanged(changeRecord);
    // Paths to snake case:
    const paths = [
      { sourcePath: '_itemData.display_name', snakePath: '_itemData.type' },
      { sourcePath: '_itemData.statuses.*.name', snakePath: '_itemData.statuses.*.status' },
      { sourcePath: '_itemData.app_attach_types.*.name', snakePath: '_itemData.app_attach_types.*.type' },
      { sourcePath: '_itemData.app_attach_types.*.attachments.*.name', snakePath: '_itemData.app_attach_types.*.attachments.*.type' },
      { sourcePath: '_itemData.warnings.*.label', snakePath: '_itemData.warnings.*.name' },
      { sourcePath: '_itemData.timers.*.label', snakePath: '_itemData.timers.*.name' }
    ];
    // Loop through, filter out irrelevant paths, and snake case values.
    paths
      .filter((x) => Path.equal(changeRecord.path, x.sourcePath))
      .forEach((x) => {
        const values = Path.getWildcardValues(changeRecord.path, x.sourcePath);
        const path = Path.setWildcardValues(x.snakePath, values);
        this.set(path, snakeCase(changeRecord.value));
      });
    // Update entry point map.
    this.updateEntryPointMap();

    this.applySchemaChangesAndNotify();
  }

  addNewAttributeGroup() {
    let sanitized = (this.newAttributeGroupName || '').trim();
    let exists = sanitized.length > 0;
    let illegal = ContainsIllegalCharacters(sanitized);
    let error = !exists
      ? 'Your attribute group must have a name'
      : illegal
        ? `You may not use the following characters: ${IllegalCharacters.join(' ')}`
        : null;
    this.$.newAttributeGroupNameInput.errorMessage = error;
    this.$.newAttributeGroupNameInput.invalid = error != null;
    if (error == null) {
      this.attributeGroupsArray.push({ $key: sanitized });
      this.saveAttributeGroups();
      this.newAttributeGroupName = '';
    }
  }
  async removeAttributeGroup(e) {
    await this.$.attributeGroupsFirebaseWorker._ref.child(e.model.item.$key).remove();
  }

  /** Click handler for button in addStatusDialog, validates and commits the change */
  addStatus() {
    const inputElement = this.$$(`#addStatusNameInput`);
    const newStatusName = inputElement.value;
    const existingStatusNames = Object.values(this._itemData.statuses ?? {}).map((x) => x.name);

    // Error if name is blank
    if (newStatusName == '') {
      inputElement.errorMessage = 'Status name cannot be blank';
      inputElement.invalid = true;
      return;
    }
    // Error if status name is duplicate
    else if (existingStatusNames.includes(newStatusName)) {
      inputElement.errorMessage = `Status name "${newStatusName}" is already in use`;
      inputElement.invalid = true;
      return;
    }

    // Otherwise, add a new entry to the statuses list
    const order = existingStatusNames.length;
    const path = `_itemData.statuses.${GeneratePushID()}`;
    this.updateProperty(new CustomEvent('add-item', { detail: { path, value: { order, name: newStatusName } } }));

    // Close the dialog on success and clear the input
    this.$$(`#addStatusDialog`).close();
    inputElement.value = '';
  }

  getEntryPoints(statusId) {
    return this.entryPointMap?.[statusId] ?? [{ none: true }];
  }

  resetAddStatusDialog() {
    const inputElement = this.$$(`#addStatusNameInput`);
    inputElement.errorMessage = '';
    inputElement.invalid = false;
  }

  labelChanged(e) {
    const label = this.get('_itemData.$label');
    this.set('_itemData.display_name', label);
    this.set('_itemData.type', snakeCase(label));
  }

  pickAnIcon(e) {
    const path = this.getDataPath(e);
    // Get a reference to the icon picker.
    const dialog = this.$.iconPickerDialog;
    const picker = dialog.querySelector('material-icon-picker');
    // Listen for one change event.
    const callback = picker.addEventListener(
      'change',
      (e) => {
        // Set the icon at the specified path.
        this.updateProperty(new CustomEvent('icon-change', { detail: { path, value: e.detail.value } }));
        // Close the dialog.
        dialog.close();
      },
      { once: true }
    );
    // Open the dialog.
    dialog.open().then(() => {
      // When the dialog closes, remove the listener (may be unecessary if listener closed the dialog).
      picker.removeEventListener('change', callback);
    });
  }

  async _computeIsPortalAdmin() {
    const uid = katapultAuth.user.uid;
    const rootCompany = await FirebaseWorker.ref(`user_groups/${uid}/root_company`)
      .once('value')
      .then((s) => s.val());
    const isPortalAdmin = await FirebaseWorker.ref(`users/${rootCompany}/${uid}/roles/pole_app_portal/pole_app_portal_admin`)
      .once('value')
      .then((s) => s.val());

    this.isPortalAdmin = isPortalAdmin;
  }

  showRenameDeleteStatusOptions(requiredStatuses, itemName) {
    const isUtility = this.userGroup == config.utilityCompany;
    return !requiredStatuses.includes(itemName) && (isUtility || this.isPortalAdmin);
  }

  promptManageStatus(e) {
    // Close the button menu
    e.currentTarget.closest('paper-menu-button')?.close();
    const status = e.model.item;

    const action = e.currentTarget.dataset.action;
    this._dialogData = {};
    this.set('_dialogData.action', action);
    this.set('_dialogData.oldStatusKey', status.$key);
    this.set('_dialogData.oldStatusName', status.name);
    this.set('_dialogData.appType', this._itemData.display_name);
    this.set('_dialogData.operationRunning', false);
    this.set(
      '_dialogData.otherStatuses',
      Object.values(this._itemData.statuses ?? {})
        .map((x) => x.name)
        .filter((x) => x != status.name)
    );

    // Open the dialog
    this.$$('#renameDeleteStatusDialog').open();
  }

  toggleOverrideEmail(e) {
    const path = this.getDataPath(e);
    const backupPath = Path.join(Path.split(path).map((x, i, arr) => (i == arr.length - 1 ? `$${x}_backup` : x)));
    const emailTemplate = this.get(path);
    if (emailTemplate) {
      // If there is an email template, clear it and save it as the backup.
      this.deepSet(backupPath, emailTemplate);
      this.deepSet(path, null);
    } else {
      // If there is not an email template, set it from the backup or initialize it.
      const backupEmailTemplate = this.get(backupPath) ?? {};
      this.deepSet(path, backupEmailTemplate);
    }
  }

  /**
   * Gather information about the item to be copied/pasted
   * @param {Event} e Event fired by the copy or paste button
   * @returns {object} An object containing the path and locationObjectType of the item to be copied/pasted
   */
  getCopyPasteInfo(e) {
    const path = this.getDataPath(e);
    const locationObjectType = e.currentTarget.dataset.copyType || 'ENTRY_ACTION';

    return {
      path,
      locationObjectType
    };
  }

  /**
   * Paste the copied item at the specified path
   * @param {Event} e Event fired by the paste button
   */
  async promptPaste(e) {
    e.currentTarget.closest('paper-menu-button')?.close();

    const pasteInfo = this.getCopyPasteInfo(e);
    const pasteLocationType = pasteInfo.locationObjectType;
    const basePastePath = pasteInfo.path;

    // Get clipboard content
    const stringifiedContent = await navigator.clipboard.readText();
    const validPasteTypes = ['STATUS', 'ACTION_BUTTON', 'ENTRY_ACTION'];
    const contentType = validPasteTypes.find((template) => stringifiedContent.startsWith(`${template}:`));
    if (!contentType) {
      this.$.toast.show('Invalid content type');
      return;
    }
    const splitContent = stringifiedContent.split(contentType, 2);
    if (splitContent.length != 2) {
      this.$.toast.show('Invalid content');
      return;
    }
    let content = JSON.parse(stringifiedContent.split(`${contentType}:`, 2)[1]);
    if (contentType != pasteLocationType) {
      this.$.toast.show(`Content of type ${contentType} cannot be pasted into a ${pasteLocationType} location`);
      return;
    }

    const stringContent = stringifiedContent.slice(`${contentType}:`.length);
    content = null;
    try {
      content = JSON.parse(stringContent);
    } catch (err) {
      this.$.toast.show('Invalid content');
      return;
    }

    // Set the content at the specified path
    this.addOrderedItem(basePastePath, content);
    const label = this.getDataLabel(content);
    this.$.toast.show(`Pasted ${CamelCase(contentType.toLowerCase())} ${label ? `"${label}"` : ''}`);
  }

  /**
   * Copies the action to the clipboard
   * @param {Event} e Event fired by the copy button
   */
  async copyAction(e) {
    const { actionId } = e.detail;
    const path = `_itemData.statuses.${e.model.item.$key}.on_entry.${actionId}`;
    this.copyData(path, 'ENTRY_ACTION');
  }

  /**
   * Copies the data at a given path to the clipboard.
   */
  async copyData(path, type) {
    if (!type) throw new Error('No type specified');
    const data = this.get(path);
    if (!data) throw new Error('No data found at path to copy');
    const copy = Path.copy(data);
    for (const key in copy) if (key.startsWith('$')) delete copy[key];
    const stringifiedContent = `${type}:${JSON.stringify(copy)}`;
    await navigator.clipboard.writeText(stringifiedContent);
    const label = this.getDataLabel(data);
    this.$.toast.show(`Copied ${CamelCase(type.toLowerCase())} ${label ? `"${label}" ` : ''}to clipboard`);
  }

  getDataLabel(data) {
    if (data.name) return data.name;
    if (data.label) return data.label;
    if (data.action) return CamelCase(data.action.toLowerCase());
    return '';
  }

  /**
   * Copies entry action or status to the clipboard
   * @param {Event} e Event fired by the copy button
   */
  async promptCopy(e) {
    e.currentTarget.closest('paper-menu-button')?.close();
    const { path, locationObjectType } = this.getCopyPasteInfo(e);
    await this.copyData(path, locationObjectType);
  }

  getAttributesForPrompt(type) {
    const attributeList = Object.entries(this.otherAttributes ?? {}).map(([key, value]) => ({ $key: key, ...value }));
    if (type == 'UPDATE_POLES') return attributeList.filter(this._isNodeAttribute);
    else return attributeList.filter(this._isJobAttribute);
  }

  updateEntryPointMap() {
    const map = {};

    const isSetStatusAction = (x) => x != null && x.action == 'SET_STATUS';

    Object.values(this._itemData?.statuses ?? {}).forEach((status) => {
      Object.values(status.on_entry ?? {})
        .filter(isSetStatusAction)
        .forEach((action) => {
          map[action.status] ??= [];
          map[action.status].push({ status: status.name, entryAction: true });
        });
      Object.values(status.action_buttons ?? {})
        .filter((x) => x != null)
        .forEach((button) => {
          Object.values(button.on_complete ?? {})
            .filter(isSetStatusAction)
            .forEach((action) => {
              map[action.status] ??= [];
              map[action.status].push({
                status: status.name,
                actionButton: { label: button.label, icon: button.icon, color: button.color }
              });
            });
        });
    });

    this.entryPointMap = map;
  }

  isDate(x) {
    let attribute = this.otherAttributes[x?.attribute];
    return attribute?.gui_element == 'date';
  }

  async renameDeleteStatus(e) {
    const action = e.currentTarget.dataset.action;
    // Get the status input identifier corresponding to the action taken
    const statusInput = this.$$({ RENAME: '#newStatusName', DELETE: '#moveStatusName' }[action]);

    // Get the value from the input, erroring if it is null
    const newStatus = statusInput.value;
    if (!newStatus) return (statusInput.invalid = true);
    else statusInput.invalid = false;
    // Get the "include archived" value
    const includeArchived = this.$$('#includeArchived')?.checked;
    // Get other data for the update
    const statusKey = this._dialogData.oldStatusKey;
    const oldStatus = this._dialogData.oldStatusName;
    const appType = this._itemData.type;
    const appTypeName = this._itemData.display_name;

    // Show the proper dialog for the selected action
    let clickedButton;
    switch (action) {
      case 'RENAME': {
        clickedButton = await DialogService.confirm({
          title: 'Confirm Rename Status',
          message:
            `Are you sure you want to rename the "${oldStatus}" status to "${newStatus}"?\n\n` +
            `This will update all${!includeArchived ? ' non-archived' : ''} ${appTypeName} apps in this status.\n\n` +
            `This action cannot be reversed.`,
          attributes: { 'max-width': 400 }
        });
        break;
      }
      case 'DELETE': {
        clickedButton = await DialogService.confirm({
          class: 'delete',
          title: 'Confirm Delete Status',
          message:
            `Are you sure you want to delete the "${oldStatus}" status?\n\n` +
            `All configuration data for this status (buttons, actions, emails etc.) will be lost. ` +
            `All${!includeArchived ? ' non-archived' : ''} ${appTypeName} apps with this status will be set to ${newStatus}.\n\n` +
            `This action cannot be reversed.`,
          attributes: { 'max-width': 400 }
        });
        break;
      }
    }

    // Check for confirmation, and if we got it, continue
    const didConfirm = clickedButton.hasAttribute('dialog-confirm');
    if (!didConfirm) return;

    this.set('_dialogData.operationRunning', true);
    try {
      // Run the mass update on the server
      const callData = { appType, oldStatus, newStatus, includeArchived };
      await firebase.functions().httpsCallable('managePortalStatuses', { timeout: 600000 })(callData);

      // Save the changes locally
      if (action == 'RENAME') {
        this.set(`_itemData.statuses.${statusKey}.name`, newStatus);
        this.set(`_itemData.statuses.${statusKey}.status`, snakeCase(newStatus));
      } else if (action == 'DELETE') {
        this.set(`_itemData.statuses.${statusKey}`, null);
      }

      // Commit all changes and update the UI
      this.fire('save-item', { forcefully: true });
      this.set('_dialogData.operationRunning', false);
      this.$$('#renameDeleteStatusDialog').close();
    } catch (err) {
      // If we encounter an error, tell the user
      this.set('_dialogData.operationRunning', false);
      console.error(err);
      DialogService.error({
        message:
          `Something went wrong! Your status change has not been applied to prevent data loss or corruption. ` +
          'The bulk update may have partially succeeded. Please check the status of your apps.\n\n' +
          'Contact Katapult Support, and provide this reporting data:\n\n' +
          err.toString(),
        attributes: { 'max-width': 600 }
      });
    }
  }

  applySchemaChangesAndNotify() {
    let hasSchemaChanges = false;

    // [October 2024] app_is_active replaced by more granular active_for_engineering and active_for_applicant
    const statusEntries = Object.entries(this._itemData?.statuses ?? {});
    for (const [statusId, status] of statusEntries) {
      if (status.active_for_engineering == null || status.active_for_applicant == null) {
        this.set(`_itemData.statuses.${statusId}.active_for_engineering`, Boolean(status.app_is_active));
        this.set(`_itemData.statuses.${statusId}.active_for_applicant`, Boolean(status.app_is_active));
        hasSchemaChanges = true;
      }
      if (status.app_is_active != null) {
        this.set(`_itemData.statuses.${statusId}.app_is_active`, null);
        hasSchemaChanges = true;
      }
    }

    // If we have schema changes and we didn't already notify the user, do so now
    if (hasSchemaChanges && !this.schemaUpdatedAlert?.opened) {
      this.schemaUpdatedAlert = KatapultDialog.alert(
        stripIndent`
          The schema for a portal app type configuration has changed. These changes have been automatically applied to this app type, and its behavior will remain unchanged.
          
          To make these changes permanent, click "Save." If you have questions, please contact support.
        `,
        'Schema Changes Detected'
      );
    }
  }
}

customElements.define('katapult-portal-app-type-editor', KatapultPortalAppTypeEditor);
